import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import { toast } from 'sonner';
import Pagination, { usePagination } from '@/components/Pagination';
import { InkomendeBetalingDialog } from '@/components/InkomendeBetalingDialog';
import { DocumentSummary } from '@/components/DocumentSummary';
import { useAuth } from '@/contexts/AuthContext';
import { useDocumentActionPreferences } from '@/hooks/useDocumentActionPreferences';
import { DocumentActionHeader } from '@/components/DocumentActionHeader';
import { InvoiceRowActions } from '@/components/InvoiceRowActions';
import '@/components/invoice-list.css';
import { UploadSimple, Trash, FilePdf, FileImage, FileText, Eye, CheckCircle, MagnifyingGlass, Plus, PencilSimple, Funnel, CurrencyEur, Lightning, SpinnerGap, DotsThreeVertical, Export, DownloadSimple, Printer, ArrowsClockwise, MagicWand, Wallet, Sparkle, CircleNotch, Warning } from '@phosphor-icons/react';

const STATUSSEN = ['', 'Te Betalen', 'Deels betaald', 'Te Domiciliëren', 'Betaald', 'Concept'];
const betaalStatusLabel = (doc) => ({ betaald: 'Betaald', deels_betaald: 'Deels betaald' }[doc.status_betaling] || doc.status || 'Te Betalen');
// Boekmap: waar hoort de factuur thuis? Enkel 'kosten' belandt in het Kostenrapport.
export const BOEKMAPPEN = [
  { key: 'kosten', label: 'Kosten', kort: 'KOSTEN', bg: '#059669' },
  { key: 'goederen', label: 'Aankoop goederen', kort: 'GOEDEREN', bg: '#0284C7' },
  { key: 'investering', label: 'Investering', kort: 'INVESTERING', bg: '#7C3AED' },
];
const boekmapInfo = (k) => BOEKMAPPEN.find(b => b.key === k);
// Hiërarchische categorie-structuur (hoofdcategorie → subcategorieën)
const CATEGORIE_GROEPEN = [
  { groep: 'Gebouwen / lokalen / kantoren / terreinen', items: ['Elektriciteit (productie + kantoor)','Gas / verwarming','Waterverbruik','Brandverzekering','Onderhoud machines (gebouwgebonden installaties)','Herstellingen gebouw','Schoonmaak (extern bedrijf)','Beveiliging (alarm, camera\'s)','Onroerende voorheffing','Syndicuskosten','Afvalverwerking / containerkosten','Internet / bekabeling in gebouw'] },
  { groep: 'Algemene kosten', items: ['Telefoon & internet abonnementen','Softwarelicenties (ERP, Billit, CAD, etc.)','Kantoorbenodigdheden','Drukwerk (labels, stickers, catalogi)','Bankkosten','Boekhouder / accountant','Advieskosten (juridisch/fiscaal)','Marketing & reclame','Website / hosting','Opleidingen','Lidmaatschappen / federaties'] },
  { groep: 'Investeringen', items: ['Machines (rollforming lijnen, decoilers)','Gereedschappen (matrijzen, molds)','IT hardware (computers, servers)','Magazijninrichting (rekken, stellingen)','Heftrucks / intern transport','Bouw of renovatie van productiehal','Zonnepanelen / energie-installaties'] },
  { groep: 'Personeel / uitzendkrachten', items: ['Lonen arbeiders & bedienden','Interimkrachten','Vakantiegeld','Bonussen','Werkkledij / PBM (veiligheidsschoenen, helmen)','Opleidingen personeel','Maaltijdcheques','Transportvergoeding'] },
  { groep: 'Administratieve formaliteiten', items: ['Notariskosten','Publicaties (Belgisch Staatsblad)','Vergunningen (VLAREM, milieu)','Certificeringen (CE, ISO)','Douanedocumenten','Juridische dossiers','Registraties (BTW, EORI)'] },
  { groep: 'Rollend materieel (andere)', items: ['Heftrucks','Magazijnvoertuigen','Palletwagens (elektrisch)','Onderhoud & herstellingen','Batterijen / laadstations'] },
  { groep: 'Onderaannemingen', items: ['Transporteurs (nationaal/internationaal)','Loonwerk (externe productie)','Montageploegen','Verpakkingsdiensten','Technisch onderhoud extern','IT outsourcing'] },
  { groep: '(Handels)goederen', items: ['Grondstoffen (staal DX51D, coils, slitband)','Halffabricaten','Inkoop profielen','Bevestigingsmaterialen (pluggen, schroeven)','Verpakkingsmateriaal (karton, folie)','Labels / private labeling'] },
  { groep: 'Huur', items: ['Huur bedrijfspand','Huur magazijn','Huur machines','Huur voertuigen','Leasing (indien als huur geboekt)'] },
  { groep: 'Rollend materieel (personenwagen)', items: ['Aankoop / leasing wagen','Brandstof wagen','Verzekering wagen','Onderhoud wagen','Banden','Taksen'] },
  { groep: 'Rollend materieel (motorfiets)', items: ['Aankoop motorfiets','Brandstof motorfiets','Onderhoud motorfiets','Verzekering motorfiets'] },
  { groep: 'Rollend materieel (lichte vracht)', items: ['Bestelwagens / camionettes','Brandstof bestelwagen','Onderhoud bestelwagen','Leasing bestelwagen','Verzekering bestelwagen','Tolkosten'] },
  { groep: 'Erelonen / commissies', items: ['Makelaars','Verkoperscommissie','Agenten','Consultancy fees','Technische adviseurs'] },
  { groep: 'Verzekeringen', items: ['BA verzekering','Arbeidsongevallen','Machinebreuk','Transportverzekering','Kredietverzekering','Brandverzekering (algemeen)'] },
  { groep: 'Sociale bijdragen', items: ['RSZ bijdragen','Zelfstandigen bijdragen','Sectorbijdragen','Pensioenfonds'] },
];

// Flat lijst — alle subcategorieën + de hoofdcategorieën zelf (voor backwards compat)
const CATEGORIEN = [
  ...CATEGORIE_GROEPEN.map(g => g.groep),
  ...CATEGORIE_GROEPEN.flatMap(g => g.items),
];
const BETAALWIJZEN_INK = ['', 'Overschrijving', 'Domiciliëring', 'Contant', 'Creditcard'];
const BTW_TYPES = ['Binnenland', 'EU', 'Buiten EU', 'Verlegd'];

export default function InkomendeDocumentenPage() {
  const { user } = useAuth();
  const actionPreferences = useDocumentActionPreferences(user, 'inkomend');
  const [docs, setDocs] = useState([]);
  const [viewMode, setViewMode] = useState('lijst'); // lijst | upload | verwerk
  const [verwerkDoc, setVerwerkDoc] = useState(null);
  const [tab, setTab] = useState('alle');
  const [q, setQ] = useState('');
  const [statusF, setStatusF] = useState('');
  const [paymentDoc, setPaymentDoc] = useState(null);
  const [paymentBusy, setPaymentBusy] = useState(false);
  // Uitgebreide filters
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [fLeverancier, setFLeverancier] = useState('');
  const [fDatumVan, setFDatumVan] = useState('');
  const [fDatumTot, setFDatumTot] = useState('');
  const [fMin, setFMin] = useState('');
  const [fMax, setFMax] = useState('');
  const [fBetaalwijze, setFBetaalwijze] = useState('');
  const [fCategorie, setFCategorie] = useState('');
  const [fMap, setFMap] = useState(''); // boekmap-filter: kosten | goederen | investering
  // Bulk selectie
  const [selected, setSelected] = useState(new Set());
  const [meerOpen, setMeerOpen] = useState(false);
  const meerRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [aiBezig, setAiBezig] = useState(null); // {index, totaal, naam} tijdens AI-lezen na toevoegen
  const [uploadMsg, setUploadMsg] = useState(null); // {type:'success'|'error', text:...}
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();
  const navigate = useNavigate();
  const openKost = (d, e) => { e?.stopPropagation?.(); navigate(`/kosten-rapport?kost=${d.kost_id}&datum=${d.datum || ''}`); };
  const KostBadge = ({ d, groot }) => d.kost_id && (d.boekmap || 'kosten') === 'kosten' ? (
    <button type="button" onClick={e => openKost(d, e)} title="Gekoppeld aan kost — open in Kostenrapport"
      className={`inline-flex items-center gap-1 font-bold bg-emerald-100 text-emerald-800 hover:bg-emerald-200 ${groot ? 'px-2 py-1 text-[11px]' : 'ml-1.5 px-1.5 py-0.5 text-[9px]'}`}
      data-testid={`kost-badge-${d.id}`}>
      <Wallet size={groot ? 13 : 10} weight="fill" /> {groot ? 'Gekoppeld aan kost · open Kostenrapport' : 'KOST'}
    </button>
  ) : null;

  // Verwerk form
  const emptyForm = {
    doc_type: 'factuur', richting: 'aankoop',
    leverancier: '', leverancier_btw: '',
    leverancier_straat: '', leverancier_huisnr: '', leverancier_postcode: '', leverancier_gemeente: '', leverancier_land: 'België',
    leverancier_email: '', leverancier_tel: '',
    categorie: 'Overige diensten', btw_type: 'Binnenland',
    iban_lev: '', factuurnummer: '', datum: new Date().toISOString().split('T')[0],
    vervaldag: '', betaald_datum: '', betaalwijze: 'Overschrijving',
    mededeling: '', grootboekrekening: '', onze_referentie: '', betreft: '',
    munteenheid: 'EUR', extra_tekst: '', interne_info: '',
    lijnen: [{ omschr: '', btw: '21', bedrag: '', totaal: '' }],
    financiele_korting: '0', status: 'Te Betalen',
  };
  const [form, setForm] = useState({ ...emptyForm });
  const [aiParsing, setAiParsing] = useState(false);
  const [aiDone, setAiDone] = useState(false);
  const [levMatch, setLevMatch] = useState(null);
  const [klanten, setKlanten] = useState([]);
  const [catMapping, setCatMapping] = useState({}); // categorie -> {grootboekrekening, btw_pct, aftrekbaarheid}

  const load = () => {
    API.get('/inkomende-documenten').then(r => setDocs(r.data)).catch(() => {});
    API.get('/klanten').then(r => setKlanten(r.data)).catch(() => {});
    API.get('/categorie-mappings').then(r => {
      const m = {};
      r.data.forEach(x => {
        m[x.categorie] = {
          grootboekrekening: x.grootboekrekening,
          btw_pct: x.btw_pct,
          aftrekbaarheid: x.aftrekbaarheid,
        };
      });
      setCatMapping(m);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Click-outside voor Meer-opties dropdown
  useEffect(() => {
    if (!meerOpen) return;
    const handler = (e) => {
      if (meerRef.current && !meerRef.current.contains(e.target)) setMeerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [meerOpen]);

  // Toevoegen → direct AI lezen → document staat 'verwerkt' in de lijst, enkel nog goedkeuren.
  const upload = async (files) => {
    const lijst = Array.from(files || []);
    if (lijst.length === 0) return;
    setUploading(true);
    setUploadMsg(null);
    let ok = 0; let aiOk = 0; let dubbel = 0; let fail = 0; const errors = []; const resultaten = [];
    for (let i = 0; i < lijst.length; i++) {
      const file = lijst[i];
      setAiBezig({ index: i + 1, totaal: lijst.length, naam: file.name });
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await API.post('/inkomende-documenten/upload-ai', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok++;
        resultaten.push(r.data);
        if (r.data?.ai_gelezen) aiOk++;
        if (r.data?.dubbel) dubbel++;
      } catch (e) {
        fail++;
        errors.push(`${file.name}: ${e.response?.data?.detail || e.message || 'upload mislukt'}`);
        console.error('[upload]', file.name, e);
      }
    }
    setAiBezig(null);
    setUploading(false);
    load();
    // Eén factuur, door AI gelezen → meteen openen zodat u enkel nog hoeft goed te keuren
    if (lijst.length === 1 && resultaten[0]?.ai_gelezen && resultaten[0]?.doc?.id) {
      if (resultaten[0].dubbel) toast.warning(`Opgelet: ${resultaten[0].dubbel.leverancier} ${resultaten[0].dubbel.factuurnummer} bestaat al`);
      navigate(`/inkomende-documenten/${resultaten[0].doc.id}`);
      return;
    }
    if (ok > 0 && fail === 0) {
      const delen = [`${ok} bestand${ok > 1 ? 'en' : ''} toegevoegd`, `${aiOk} door AI gelezen — klaar om goed te keuren`];
      if (ok - aiOk > 0) delen.push(`${ok - aiOk} niet leesbaar (staat bij "Nog te verwerken")`);
      if (dubbel > 0) delen.push(`${dubbel} mogelijk dubbel`);
      setUploadMsg({ type: dubbel > 0 || ok - aiOk > 0 ? 'warning' : 'success', text: delen.join(' · ') });
      setTimeout(() => { setUploadMsg(null); setViewMode('lijst'); }, 3500);
    } else if (ok > 0 && fail > 0) {
      setUploadMsg({ type: 'warning', text: `${ok} toegevoegd (${aiOk} door AI gelezen), ${fail} mislukt: ${errors.join('; ')}` });
    } else {
      setUploadMsg({ type: 'error', text: `Upload mislukt: ${errors.join('; ')}` });
    }
  };

  // Snel goedkeuren vanuit de lijst (AI-gelezen rij) — met gekozen boekmap
  const snelGoedkeuren = async (d, e) => {
    e?.stopPropagation?.();
    const map = d.boekmap || 'kosten';
    if (d.dubbel_van && !window.confirm(`Mogelijk dubbel: ${d.dubbel_van.leverancier} ${d.dubbel_van.factuurnummer || ''}. Heeft u deze nagezien en wilt u toch goedkeuren?`)) return;
    try {
      const r = await API.post(`/inkomende-documenten/${d.id}/goedkeuren`, { boekmap: map, bevestig_dubbel:!!d.dubbel_van });
      if (r.data?.boekmap && r.data.boekmap !== 'kosten') toast.success(`Goedgekeurd en geklasseerd onder "${r.data.boekmap_label}" — geen kost aangemaakt`);
      else if (r.data?.aangemaakt) toast.success('Goedgekeurd — kost aangemaakt in Kostenrapport');
      else if (r.data?.reden) toast.warning(`Goedgekeurd, maar geen kost: ${r.data.reden}`);
      else toast.success('Goedgekeurd');
      if (r.data?.kost_bestaand) toast.warning(`Let op: dit document stond al als kost ${r.data.kost_bestaand} in het Kostenrapport — verwijder die daar indien nodig`, { duration: 9000 });
      load();
    } catch (e2) { toast.error(e2.response?.data?.detail || 'Goedkeuren mislukt'); }
  };
  const zetBoekmap = async (d, map, e) => {
    e?.stopPropagation?.();
    setDocs(prev => prev.map(x => x.id === d.id ? { ...x, boekmap: map } : x));
    try { await API.put(`/inkomende-documenten/${d.id}`, { boekmap: map }); } catch { toast.error('Boekmap opslaan mislukt'); }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) upload(e.dataTransfer.files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const delDoc = async (id) => { await API.delete(`/inkomende-documenten/${id}`).catch(() => {}); load(); };

  const startVerwerk = (d) => {
    // Nieuwe detailpagina met split-view PDF preview
    navigate(`/inkomende-documenten/${d.id}`);
  };

  // eslint-disable-next-line no-unused-vars
  const _legacyStartVerwerk = (d) => {
    setVerwerkDoc(d);
    // Als document al verwerkt is, laad de bestaande gegevens
    if (d.leverancier || d.status_doc === 'verwerkt') {
      setForm({
        ...emptyForm,
        doc_type: d.doc_type || 'factuur',
        richting: d.richting || 'aankoop',
        leverancier: d.leverancier || '',
        categorie: d.categorie || 'Overige diensten',
        btw_type: d.btw_type || 'Binnenland',
        iban_lev: d.iban_lev || '',
        factuurnummer: d.factuurnummer || '',
        datum: d.datum || new Date().toISOString().split('T')[0],
        vervaldag: d.vervaldag || '',
        betaald_datum: d.betaald_datum || '',
        betaalwijze: d.betaalwijze || 'Overschrijving',
        mededeling: d.mededeling || '',
        grootboekrekening: d.grootboekrekening || '',
        onze_referentie: d.onze_referentie || '',
        betreft: d.betreft || '',
        munteenheid: d.munteenheid || 'EUR',
        extra_tekst: d.extra_tekst || '',
        interne_info: d.interne_info || '',
        lijnen: d.lijnen?.length ? d.lijnen : [{ omschr: '', btw: '21', bedrag: '', totaal: '' }],
        financiele_korting: String(d.financiele_korting || '0'),
        status: d.status || 'Te Betalen',
      });
    } else {
      setForm({
        ...emptyForm,
        factuurnummer: d.filename?.replace(/\.\w+$/, '') || '',
        datum: (d.uploaded_at || '').split('T')[0] || new Date().toISOString().split('T')[0],
      });
    }
    setViewMode('verwerk');
  };

  const ublParse = async () => {
    if (!verwerkDoc) return;
    setAiParsing(true);
    try {
      const r = await API.post(`/inkomende-documenten/${verwerkDoc.id}/ubl-parse`);
      const d = r.data?.data;
      if (d) {
        setForm(p => ({
          ...p,
          leverancier: d.leverancier || p.leverancier,
          iban_lev: d.leverancier_iban || p.iban_lev,
          factuurnummer: d.factuurnummer || p.factuurnummer,
          datum: d.datum || p.datum,
          vervaldag: d.vervaldag || p.vervaldag,
          mededeling: d.mededeling || p.mededeling,
          munteenheid: d.munteenheid || p.munteenheid,
          lijnen: (d.lijnen && d.lijnen.length > 0) ? d.lijnen : p.lijnen,
        }));
        setAiDone(true);
        // Auto-match leverancier na UBL parse
        try {
          await API.put(`/inkomende-documenten/${verwerkDoc.id}`, {
            leverancier: d.leverancier || '', leverancier_btw: d.leverancier_btw || '',
          });
          const mr = await API.post(`/inkomende-documenten/${verwerkDoc.id}/match-leverancier`);
          if (mr.data?.matched) {
            setLevMatch({ matched: true, klant: mr.data.klant, type: mr.data.match_type, regel: mr.data.regel_toegepast });
            if (mr.data.klant?.iban) setForm(p => ({ ...p, iban_lev: mr.data.klant.iban || p.iban_lev }));
          } else {
            setLevMatch({ matched: false });
          }
        } catch { /* leverancier-match silent fail */ }
        setVerwerkDoc(p => ({ ...p, is_peppol: true, status_doc: 'verwerkt' }));
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'UBL-parsing mislukt.');
    }
    setAiParsing(false);
  };

  const aiParse = async () => {
    if (!verwerkDoc) return;
    setAiParsing(true); setAiDone(false);
    try {
      const r = await API.post(`/inkomende-documenten/${verwerkDoc.id}/ai-parse`);
      const d = r.data?.data;
      if (d) {
        setForm(p => ({
          ...p,
          leverancier: d.leverancier || p.leverancier,
          leverancier_btw: d.leverancier_btw || p.leverancier_btw,
          leverancier_straat: d.leverancier_straat || p.leverancier_straat,
          leverancier_huisnr: d.leverancier_huisnr || p.leverancier_huisnr,
          leverancier_postcode: d.leverancier_postcode || p.leverancier_postcode,
          leverancier_gemeente: d.leverancier_gemeente || p.leverancier_gemeente,
          leverancier_land: d.leverancier_land || p.leverancier_land,
          leverancier_email: d.leverancier_email || p.leverancier_email,
          leverancier_tel: d.leverancier_tel || p.leverancier_tel,
          iban_lev: d.leverancier_iban || p.iban_lev,
          factuurnummer: d.factuurnummer || p.factuurnummer,
          datum: d.datum || p.datum,
          vervaldag: d.vervaldag || p.vervaldag,
          mededeling: d.mededeling || p.mededeling,
          categorie: d.categorie || p.categorie,
          betreft: d.betreft || p.betreft,
          lijnen: (d.lijnen && d.lijnen.length > 0) ? d.lijnen.map(l => ({
            omschr: l.omschr || '', btw: String(l.btw || '21'),
            bedrag: String(l.bedrag || ''),
            totaal: ((parseFloat(l.bedrag) || 0) * (1 + (parseFloat(l.btw) || 0) / 100)).toFixed(2)
          })) : p.lijnen,
        }));
        setAiDone(true);
        // Auto-match leverancier na AI parse
        try {
          // First save AI data to doc
          await API.put(`/inkomende-documenten/${verwerkDoc.id}`, {
            leverancier: d.leverancier || '', leverancier_btw: d.leverancier_btw || '',
          });
          const mr = await API.post(`/inkomende-documenten/${verwerkDoc.id}/match-leverancier`);
          if (mr.data?.matched) {
            setLevMatch({ matched: true, klant: mr.data.klant, type: mr.data.match_type, regel: mr.data.regel_toegepast });
            if (mr.data.klant?.iban) setForm(p => ({ ...p, iban_lev: mr.data.klant.iban || p.iban_lev }));
          } else {
            setLevMatch({ matched: false });
          }
        } catch { /* leverancier-match silent fail */ }
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'AI-analyse mislukt. Probeer opnieuw of vul handmatig in.');
    }
    setAiParsing(false);
  };

  const saveVerwerk = async () => {
    const excl = (form.lijnen || []).reduce((s, l) => s + (parseFloat(l.bedrag) || 0), 0);
    const korting = parseFloat(form.financiele_korting) || 0;
    const btwPct = (form.lijnen || []).reduce((s, l) => s + (parseFloat(l.btw) || 0), 0) / Math.max(form.lijnen.length, 1);
    const btwBedrag = (excl - korting) * btwPct / 100;
    const totaal = excl - korting + btwBedrag;

    // Auto-aanmaak van leverancier als deze niet bestaat in database
    let klant_id = levMatch?.klant?.id || '';
    if (!klant_id && form.leverancier) {
      try {
        const newKlant = {
          bedrijf: form.leverancier,
          btwnr: form.leverancier_btw || '',
          iban: form.iban_lev || '',
          email: form.leverancier_email || '',
          tel: form.leverancier_tel || '',
          straat: form.leverancier_straat || '',
          huisnr: form.leverancier_huisnr || '',
          postcode: form.leverancier_postcode || '',
          gemeente: form.leverancier_gemeente || '',
          land: form.leverancier_land || 'België',
          is_leverancier: true,
          is_klant: false,
          bron: 'auto_inkomend',
        };
        const r = await API.post('/klanten', newKlant);
        klant_id = r.data?.id || '';
        // Refresh klanten list + show notification
        setLevMatch({ matched: true, klant: r.data, type: 'nieuw_aangemaakt', regel: false });
        API.get('/klanten').then(res => setKlanten(res.data)).catch(() => {});
      } catch (e) {
        console.error('[auto-lev] aanmaken mislukt', e);
      }
    }

    const payload = {
      ...form, excl: Math.round(excl * 100) / 100,
      btw_bedrag: Math.round(btwBedrag * 100) / 100,
      totaal: Math.round(totaal * 100) / 100,
      te_betalen: Math.round(totaal * 100) / 100,
      bron_doc_id: verwerkDoc?.id || '',
      klant_id,
    };

    try {
      if (verwerkDoc) {
        await API.put(`/inkomende-documenten/${verwerkDoc.id}`, { ...payload, status_doc: 'verwerkt' });
      }
      await API.post('/inkomende-facturen', payload);
      setViewMode('lijst');
      setVerwerkDoc(null);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Fout bij opslaan'); }
  };

  const goedkeuren = async (docId) => {
    const target = docs.find(d=>d.id===(docId||verwerkDoc?.id)) || verwerkDoc;
    if(target?.dubbel_van && !window.confirm('Mogelijk dubbele factuur. Heeft u deze nagezien en wilt u toch goedkeuren?'))return;
    try {
      const r = await API.post(`/inkomende-documenten/${docId || verwerkDoc?.id}/goedkeuren`,{bevestig_dubbel:!!target?.dubbel_van});
      if (r.data?.aangemaakt) toast.success('Kost automatisch aangemaakt in Kostenrapport (factuurdatum)');
      else if (r.data?.reden) toast.warning(`Geen kost aangemaakt: ${r.data.reden}`);
      load();
      if (verwerkDoc) setVerwerkDoc(p => ({ ...p, status_doc: 'goedgekeurd', kost_id: r.data?.kost_id || p.kost_id }));
    } catch (e) { alert(e.response?.data?.detail || 'Goedkeuren mislukt'); }
  };

  const boeken = async (docId) => {
    try {
      const r = await API.post(`/inkomende-documenten/${docId || verwerkDoc?.id}/boeken`);
      if (r.data?.aangemaakt) toast.success('Kost automatisch aangemaakt in Kostenrapport (factuurdatum)');
      load();
      if (verwerkDoc) setVerwerkDoc(p => ({ ...p, status_doc: 'geboekt', kost_id: r.data?.kost_id || p.kost_id }));
    } catch (e) { alert(e.response?.data?.detail || 'Boeken mislukt'); }
  };

  const afkeuren = async (docId) => {
    const reden = prompt('Reden voor afkeuring:');
    if (reden === null) return;
    try {
      await API.post(`/inkomende-documenten/${docId || verwerkDoc?.id}/afkeuren`, { reden });
      load();
      if (verwerkDoc) setVerwerkDoc(p => ({ ...p, status_doc: 'afgekeurd' }));
    } catch (e) { alert(e.response?.data?.detail || 'Afkeuren mislukt'); }
  };

  const PIPELINE = [
    { key: 'te_verwerken', label: 'Te verwerken', color: 'bg-yellow-100 text-yellow-800' },
    { key: 'verwerkt', label: 'Goed te keuren', color: 'bg-blue-100 text-blue-800' },
    { key: 'goedgekeurd', label: 'Goedgekeurd', color: 'bg-green-100 text-green-800' },
    { key: 'geboekt', label: 'Geboekt', color: 'bg-purple-100 text-purple-800' },
    { key: 'afgekeurd', label: 'Afgekeurd', color: 'bg-red-100 text-red-800' },
  ];
  const pipelineBadge = (s) => PIPELINE.find(p => p.key === s) || PIPELINE[0];
  const u = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const updLijn = (i, k, v) => setForm(p => {
    const lijnen = p.lijnen.map((l, j) => {
      if (j !== i) return l;
      const nl = { ...l, [k]: v };
      if (k === 'bedrag' || k === 'btw') {
        const bed = parseFloat(nl.bedrag) || 0;
        const btw = parseFloat(nl.btw) || 0;
        nl.totaal = (bed * (1 + btw / 100)).toFixed(2);
      }
      return nl;
    });
    return { ...p, lijnen };
  });
  const addLijn = () => setForm(p => ({ ...p, lijnen: [...p.lijnen, { omschr: '', btw: '21', bedrag: '', totaal: '' }] }));
  const rmLijn = (i) => setForm(p => ({ ...p, lijnen: p.lijnen.filter((_, j) => j !== i) }));

  // Filters
  const filtered = docs.filter(d => {
    // Tab filters
    if (tab === 'vervallen_tab') return d.vervaldag && new Date(d.vervaldag) < new Date() && d.status_doc !== 'geboekt' && d.status !== 'Betaald';
    if (tab === 'bonnetjes') return d.doc_type === 'bonnetje';
    if (tab === 'te_verwerken') return !d.leverancier && (!d.status_doc || d.status_doc === 'te_verwerken');
    if (tab === 'verwerkt') return d.status_doc === 'verwerkt' || (!!d.leverancier && (!d.status_doc || d.status_doc === 'te_verwerken')) || (d.dubbel_van && d.auto_goedgekeurd && !d.dubbel_bevestigd);
    if (tab === 'goedgekeurd') return d.status_doc === 'goedgekeurd';
    if (tab === 'geboekt') return d.status_doc === 'geboekt';
    return true;
  }).filter(d => !statusF || betaalStatusLabel(d) === statusF)
    .filter(d => !fMap || (d.boekmap || 'kosten') === fMap)
    .filter(d => !fLeverancier || (d.leverancier || '').toLowerCase().includes(fLeverancier.toLowerCase()))
    .filter(d => !fBetaalwijze || d.betaalwijze === fBetaalwijze)
    .filter(d => {
      if (!fCategorie) return true;
      // Match op exacte categorie OF op hoofdcategorie (dan match ook alle subcategorieën van die groep)
      if (d.categorie === fCategorie) return true;
      const groep = CATEGORIE_GROEPEN.find(g => g.groep === fCategorie);
      if (groep && groep.items.includes(d.categorie)) return true;
      return false;
    })
    .filter(d => !fDatumVan || (d.datum && d.datum >= fDatumVan))
    .filter(d => !fDatumTot || (d.datum && d.datum <= fDatumTot))
    .filter(d => {
      if (!fMin && !fMax) return true;
      const t = parseFloat(d.totaal) || 0;
      if (fMin && t < parseFloat(fMin)) return false;
      if (fMax && t > parseFloat(fMax)) return false;
      return true;
    })
    .filter(d => !q || `${d.leverancier} ${d.factuurnummer} ${d.filename} ${d.betreft} ${d.onze_referentie}`.toLowerCase().includes(q.toLowerCase()));

  // Paginatie op de verwerkte hoofdrijen (het upload/onverwerkt-sectie blijft onbeperkt — typisch <20)
  const verwerkteRijen = useMemo(
    () => {
      const arr = filtered.filter(d => d.status_doc === 'verwerkt' || d.status_doc === 'goedgekeurd' || d.status_doc === 'geboekt' || d.status_doc === 'afgekeurd' || d.leverancier);
      // Sorteer: laatste factuur ALTIJD boven (datum desc, met fallback op created_at)
      return [...arr].sort((a, b) => {
        const da = a.datum || a.created_at || '';
        const db = b.datum || b.created_at || '';
        return db.localeCompare(da);
      });
    },
    [filtered]
  );
  // Nog te verwerken uploads — ruwe documenten zonder leverancier-info (vóór AI-parsing)
  const nogTeVerwerkenRijen = useMemo(
    () => filtered.filter(d => !d.leverancier && d.status_doc !== 'verwerkt' && d.status_doc !== 'goedgekeurd' && d.status_doc !== 'geboekt' && d.status_doc !== 'afgekeurd'),
    [filtered]
  );
  // Totaal openstaand bedrag over alle verwerkte rijen (niet betaald, niet afgekeurd)
  const totaalOpenstaand = useMemo(() => {
    let openstaand = 0, aantal = 0;
    verwerkteRijen.forEach(d => {
      if (d.status_doc === 'afgekeurd') return;
      if (d.status_betaling === 'betaald') return;
      const saldo = Number(d.openstaand ?? 0);
      if (saldo > 0.005) { openstaand += saldo; aantal++; }
    });
    return { bedrag: openstaand, aantal };
  }, [verwerkteRijen]);
  const { pageSize, setPageSize, curPage, setPage, paged: pagedVerwerkt } = usePagination(verwerkteRijen, 'inkomende_facturen');

  const hasActiveFilters = fLeverancier || fDatumVan || fDatumTot || fMin || fMax || fBetaalwijze || fCategorie || statusF || fMap;
  const clearFilters = () => {
    setFLeverancier(''); setFDatumVan(''); setFDatumTot(''); setFMin(''); setFMax('');
    setFBetaalwijze(''); setFCategorie(''); setStatusF(''); setFMap('');
  };

  // Bulk acties
  const toggleSel = (id) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const toggleSelAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(d => d.id)));
  };
  const bulkMarkBetaald = async () => {
    if (selected.size === 0 || paymentBusy) return;
    const eligible = docs.filter(d => selected.has(d.id) && d.openstaand > 0 && d.status_doc !== 'afgekeurd' && d.doc_type !== 'creditnota');
    if (!eligible.length) { toast.info('Geen openstaande facturen in de selectie'); return; }
    if (eligible.length === 1) { setPaymentDoc(eligible[0]); return; }
    if (!window.confirm(`Bevestig dat het volledige openstaande saldo van ${eligible.length} facturen vandaag is betaald (${fmt(eligible.reduce((sum, d) => sum + d.openstaand, 0))}). Er wordt geen geld overgemaakt.`)) return;
    setPaymentBusy(true);
    let success = 0;
    for (const d of eligible) {
      try {
        await API.post(`/inkomende-documenten/${d.id}/markeer-betaald`, { bedrag: d.openstaand, betaaldatum: new Date().toLocaleDateString('sv-SE'), betaling_id: crypto.randomUUID() });
        success++;
      } catch (e) { toast.error(`${d.factuurnummer || d.id}: ${e.response?.data?.detail || 'Bevestigen mislukt'}`); }
    }
    if (success) toast.success(`${success} betaalbevestiging(en) geregistreerd`);
    setPaymentBusy(false);
    setSelected(new Set());
    load();
  };
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [catModalValue, setCatModalValue] = useState('');
  const bulkSetCategorie = () => {
    if (selected.size === 0) return;
    setCatModalValue('');
    setCatModalOpen(true);
  };
  const applyCategorie = async () => {
    if (!catModalValue) { alert('Kies een categorie'); return; }
    for (const id of selected) {
      await API.put(`/inkomende-documenten/${id}`, { categorie: catModalValue }).catch(() => {});
    }
    setSelected(new Set());
    setCatModalOpen(false);
    load();
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} document(en) verwijderen? Dit kan niet ongedaan worden gemaakt.`)) return;
    for (const id of selected) {
      await API.delete(`/inkomende-documenten/${id}`).catch(() => {});
    }
    setSelected(new Set());
    load();
  };

  // "Meer opties" acties
  const getSelectedDocs = () => docs.filter(d => selected.has(d.id));

  const bulkExport = () => {
    const sel = getSelectedDocs();
    if (sel.length === 0) return;
    const headers = ['Factuurnummer','Datum','Vervaldag','Leverancier','BTW-nummer','IBAN','Categorie','Betreft','Excl.','BTW','Totaal','Te betalen','Status','Betaalwijze','Mededeling','Referentie'];
    const escape = (v) => {
      const s = String(v ?? '');
      return /[",;\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const rows = sel.map(d => [
      d.factuurnummer || '', d.datum || '', d.vervaldag || '',
      d.leverancier || '', d.leverancier_btw || '', d.iban_lev || '',
      d.categorie || '', d.betreft || d.filename || '',
      d.excl ?? '', d.btw_bedrag ?? '', d.totaal ?? '', d.te_betalen ?? '',
      d.status || d.status_doc || '', d.betaalwijze || '',
      d.mededeling || '', d.onze_referentie || ''
    ].map(escape).join(';'));
    const csv = [headers.join(';'), ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inkomende-facturen-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setMeerOpen(false);
  };

  const bulkDownload = async () => {
    const sel = getSelectedDocs();
    if (sel.length === 0) return;
    // Download elk bestand in een nieuw tabblad (browser handelt af)
    for (const d of sel) {
      if (d.stored) {
        const a = document.createElement('a');
        a.href = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${d.stored}`;
        a.download = d.filename || d.stored;
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        await new Promise(r => setTimeout(r, 150));
      }
    }
    setMeerOpen(false);
  };

  const bulkPrint = () => {
    const sel = getSelectedDocs();
    if (sel.length === 0) return;
    // Open elk PDF-bestand in een nieuw tabblad voor afdrukken
    const pdfs = sel.filter(d => d.type === 'pdf');
    if (pdfs.length === 0) {
      alert('Geen PDF-bestanden geselecteerd om te printen');
      setMeerOpen(false);
      return;
    }
    pdfs.forEach(d => {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${d.stored}`, '_blank');
    });
    setMeerOpen(false);
  };

  const bulkToggleBetaald = async () => {
    setMeerOpen(false);
    await bulkMarkBetaald();
  };

  // 📎 Bulk merge geselecteerde PDF's tot 1 nieuw document
  const [mergingBulk, setMergingBulk] = useState(false);
  const bulkMerge = async () => {
    const sel = getSelectedDocs();
    if (sel.length < 2) {
      alert('Selecteer minstens 2 documenten om samen te voegen');
      return;
    }
    const pdfs = sel.filter(d => (d.type || '').toLowerCase() === 'pdf');
    const nonPdf = sel.length - pdfs.length;
    if (pdfs.length < 2) {
      alert(`Slechts ${pdfs.length} PDF-document(en) in selectie — minstens 2 PDFs vereist.`);
      return;
    }
    // Volgorde van selectie zoals de gebruiker ze aanklikte (uit `selected` Set, geconverteerd naar array volgens insertion order)
    const orderedIds = Array.from(selected).filter(id => pdfs.some(p => p.id === id));
    const eersteLev = pdfs[0]?.leverancier || pdfs[0]?.filename || '?';
    if (!window.confirm(
      `📎 ${pdfs.length} PDF(s) samenvoegen tot 1 nieuw document?\n\n` +
      orderedIds.map((id, i) => {
        const d = pdfs.find(p => p.id === id) || {};
        return `${i+1}. ${d.filename || d.id}`;
      }).join('\n') +
      `\n\n• Metadata wordt overgenomen van: ${eersteLev}\n` +
      `• Dubbele PDFs (zelfde inhoud) worden automatisch overgeslagen (SHA-256)\n` +
      `• De originele losse documenten worden verwijderd\n` +
      (nonPdf > 0 ? `\n⚠ ${nonPdf} niet-PDF item(s) worden NIET meegenomen` : '')
    )) return;
    setMergingBulk(true);
    setMeerOpen(false);
    try {
      const r = await API.post('/inkomende-documenten/merge', { ids: orderedIds, dedup_hash: true });
      const d = r.data || {};
      let msg = `✓ ${d.merged_count} PDF(s) samengevoegd tot ${d.total_pages} pagina's`;
      if ((d.skipped_duplicates || []).length > 0) {
        msg += `\n\n🔁 ${d.skipped_duplicates.length} duplicaat(en) automatisch overgeslagen:\n` +
          d.skipped_duplicates.map(s => `  • ${s.filename}`).join('\n');
      }
      if ((d.skipped_non_pdf || []).length > 0) {
        msg += `\n\n⚠ ${d.skipped_non_pdf.length} document(en) niet meegenomen:\n` +
          d.skipped_non_pdf.map(s => `  • ${s.filename} (${s.reden})`).join('\n');
      }
      // 🤖 AI-parse op de nieuwe samengevoegde PDF — en persist meteen naar DB
      const newId = d.new_doc?.id;
      if (newId) {
        msg += `\n\n🤖 AI analyseert nu het samengevoegde document...`;
        try {
          const aiR = await API.post(`/inkomende-documenten/${newId}/ai-parse`);
          const ex = aiR.data?.data;
          if (ex && typeof ex === 'object') {
            // Verwijder interne velden, behoud business-velden
            const { _regel_toegepast, _duplicaat, _leverancier_id, _leverancier_enriched, raw_response, ...persistable } = ex;
            await API.put(`/inkomende-documenten/${newId}`, persistable);
            msg += `\n✓ AI-parse voltooid — leverancier "${ex.leverancier || '?'}" • factuurnr ${ex.factuurnummer || '?'} • totaal € ${ex.totaal || ex.totaal_incl || '?'} (lijnen: ${(ex.lijnen||[]).length})`;
            if (_duplicaat) {
              msg += `\n⚠ Mogelijk duplicaat van factuur ${_duplicaat.match?.factuurnummer || '?'} (${_duplicaat.type})`;
            }
          } else {
            msg += `\n⚠ AI gaf geen geldige data terug — open het document om handmatig in te vullen`;
          }
        } catch (aiErr) {
          msg += `\n⚠ AI-parse mislukt: ${aiErr.response?.data?.detail || aiErr.message} — vul handmatig in`;
        }
      }
      alert(msg);
      setSelected(new Set());
      load();
    } catch (e) {
      alert('Samenvoegen mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setMergingBulk(false);
  };

  const fileUrl = (d) => `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${d.stored}`;

  // ═══ VERWERKSCHERM ═══
  if (viewMode === 'verwerk' && verwerkDoc) {
    const ext = (verwerkDoc.type || '').toLowerCase();
    const isImage = ['png','jpg','jpeg','gif','tif','heic'].includes(ext);
    const excl = (form.lijnen || []).reduce((s, l) => s + (parseFloat(l.bedrag) || 0), 0);
    const korting = parseFloat(form.financiele_korting) || 0;
    const avgBtw = (form.lijnen || []).length > 0 ? form.lijnen.reduce((s, l) => s + (parseFloat(l.btw) || 0), 0) / form.lijnen.length : 21;
    const btwBedrag = (excl - korting) * avgBtw / 100;
    const totaal = excl - korting + btwBedrag;
    const directUrl = fileUrl(verwerkDoc);

    return (
      <div data-testid="verwerk-page">
        {/* Pipeline stappen */}
        <div className="flex items-center gap-1 mb-3 px-1">
          {PIPELINE.filter(p => p.key !== 'afgekeurd').map((p, i) => {
            const currentIdx = PIPELINE.findIndex(x => x.key === (verwerkDoc?.status_doc || 'te_verwerken'));
            const stepIdx = i;
            const isActive = stepIdx === currentIdx;
            const isDone = stepIdx < currentIdx;
            return (
              <div key={p.key} className="flex items-center gap-1">
                {i > 0 && <div className={`w-8 h-0.5 ${isDone ? 'bg-[#008A27]' : 'bg-gray-200'}`} />}
                <div className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold transition-all ${
                  isActive ? p.color + ' ring-2 ring-offset-1 ring-current' : isDone ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-300'
                }`}>
                  {isDone && <CheckCircle size={13} weight="bold" />}
                  {p.label}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between mb-3">
          <button className="btn btn-ghost text-sm" onClick={() => { setViewMode('lijst'); setVerwerkDoc(null); setAiDone(false); setLevMatch(null); }}>Terug naar lijst</button>
          <div className="flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={aiParse} disabled={aiParsing} data-testid="ai-parse-btn">
              {aiParsing ? <><SpinnerGap size={14} className="animate-spin" /> Analyseren...</> : aiDone ? <><CheckCircle size={14} weight="bold" /> AI Ingevuld</> : <><Lightning size={14} weight="bold" /> AI Uitlezen</>}
            </button>
            {(verwerkDoc?.type || '').toLowerCase() === 'xml' && (
              <button className="btn btn-sm text-white" style={{ background: '#0891B2' }} onClick={ublParse} disabled={aiParsing} data-testid="ubl-parse-btn">
                <Lightning size={14} weight="bold" /> UBL/Peppol Uitlezen
              </button>
            )}
            {(!verwerkDoc?.status_doc || verwerkDoc?.status_doc === 'te_verwerken' || verwerkDoc?.status_doc === 'afgekeurd') && (
              <button className="btn btn-secondary btn-sm" onClick={saveVerwerk} data-testid="save-verwerk-btn">Opslaan</button>
            )}
            {(verwerkDoc?.status_doc === 'verwerkt') && (
              <>
                <button className="btn btn-sm text-white" style={{ background: '#008A27' }} onClick={() => goedkeuren()} data-testid="goedkeuren-btn">Goedkeuren</button>
                <button className="btn btn-ghost btn-sm text-red-500" onClick={() => afkeuren()} data-testid="afkeuren-btn">Afkeuren</button>
              </>
            )}
            {(verwerkDoc?.status_doc === 'goedgekeurd') && (
              <button className="btn btn-sm text-white" style={{ background: '#7C3AED' }} onClick={() => boeken()} data-testid="boeken-btn">Boeken</button>
            )}
            <button className="btn btn-ghost btn-sm text-red-500" onClick={() => { delDoc(verwerkDoc.id); setViewMode('lijst'); }}>Wissen</button>
          </div>
        </div>

        {verwerkDoc?.kost_id && (
          <div className="mb-3 p-2.5 text-xs flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800" data-testid="kost-banner">
            <Wallet size={16} weight="fill" /> Deze factuur staat als kost in het Kostenrapport. <KostBadge d={verwerkDoc} groot />
          </div>
        )}
        {/* Leverancier match indicator */}
        {levMatch && (
          <div className={`mb-3 p-2.5 text-xs flex items-center gap-2 ${levMatch.matched ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-blue-50 border border-blue-200 text-blue-800'}`} data-testid="lev-match-banner">
            {levMatch.matched ? (
              <><CheckCircle size={16} weight="bold" /> Leverancier herkend: <strong>{levMatch.klant?.bedrijf || levMatch.klant?.voorn}</strong> (via {levMatch.type}){levMatch.regel && <span className="ml-1 px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold">Boekingsregel toegepast</span>}</>
            ) : (
              <><Plus size={14} weight="bold" /> Nieuwe leverancier — <strong>{form.leverancier || '(naam invullen)'}</strong> wordt automatisch aangemaakt bij Opslaan</>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" style={{ minHeight: 'calc(100vh - 140px)' }}>
          {/* Links: PDF/bestand preview */}
          <div className="bg-white border border-gray-200 flex flex-col overflow-hidden" style={{ maxHeight: 'calc(100vh - 140px)' }}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50">
              <span className="text-xs font-medium text-gray-600 truncate">{verwerkDoc.filename}</span>
              <a href={directUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm text-xs"><Eye size={13} /> Openen</a>
            </div>
            <div className="flex-1 flex items-center justify-center p-4 overflow-auto bg-[#f5f5f5]">
              {isImage ? (
                <img src={directUrl} alt={verwerkDoc.filename} className="max-w-full max-h-full object-contain" />
              ) : ext === 'pdf' ? (
                <object data={directUrl} type="application/pdf" className="w-full h-full" style={{ minHeight: 600 }}>
                  <div className="text-center py-16">
                    <FilePdf size={64} className="text-[#DC2626] mx-auto mb-3" />
                    <p className="text-sm text-gray-500 mb-2">PDF kan niet worden weergegeven</p>
                    <a href={directUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">PDF openen in nieuw tabblad</a>
                  </div>
                </object>
              ) : (
                <div className="text-center py-16">
                  <FileText size={64} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">{verwerkDoc.filename}</p>
                  <a href={directUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm mt-2">Bestand openen</a>
                </div>
              )}
            </div>
          </div>

          {/* Rechts: Formulier */}
          <div className="bg-white border border-gray-200 overflow-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              {['Factuur', 'Bonnetje', 'Kredietkaart', 'Document'].map(t => (
                <button key={t} className={`flex-1 py-2 text-xs font-bold text-center ${form.doc_type === t.toLowerCase() ? 'bg-[#F0F4FF] text-[#002FA7] border-b-2 border-[#002FA7]' : 'text-gray-400 hover:bg-gray-50'}`}
                  onClick={() => setForm(p => ({ ...p, doc_type: t.toLowerCase() }))}>{t}</button>
              ))}
            </div>

            <div className="p-4 space-y-3">
              {/* Aankoop / Verkoop */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-28">Aankoop / Verkoop</span>
                <div className="flex">
                  <button className={`px-4 py-1 text-xs font-bold ${form.richting === 'aankoop' ? 'bg-[#D97706] text-white' : 'bg-gray-100 text-gray-500'}`}
                    onClick={() => setForm(p => ({ ...p, richting: 'aankoop' }))}>Aankoop</button>
                  <button className={`px-4 py-1 text-xs font-bold ${form.richting === 'verkoop' ? 'bg-[#D97706] text-white' : 'bg-gray-100 text-gray-500'}`}
                    onClick={() => setForm(p => ({ ...p, richting: 'verkoop' }))}>Verkoop</button>
                </div>
              </div>

              {/* Velden */}
              <div className="space-y-2.5">
                <div className="flex items-center gap-2"><span className="text-xs text-gray-500 w-28">Leverancier *</span><input className="field-input flex-1" value={form.leverancier} onChange={u('leverancier')} placeholder="Zoek leverancier..." data-testid="vw-leverancier" /></div>
                <div className="flex items-center gap-2"><span className="text-xs text-gray-500 w-28">Categorie *</span><select className="field-input flex-1" value={form.categorie} onChange={e => {
                  const nieuw = e.target.value;
                  const nw = catMapping[nieuw] || {};
                  const oud = catMapping[form.categorie] || {};
                  setForm(p => {
                    // Auto-fill grootboekrekening (als leeg of uit oude mapping kwam)
                    const nieuwGb = (!p.grootboekrekening || p.grootboekrekening === oud.grootboekrekening) ? (nw.grootboekrekening || p.grootboekrekening) : p.grootboekrekening;
                    // Auto-update BTW% op elke regel (alleen als niet Vrijgesteld)
                    const nieuwBtw = nw.btw_pct;
                    let nieuweLijnen = p.lijnen;
                    if (nieuwBtw !== undefined && nieuwBtw !== -1) {
                      nieuweLijnen = (p.lijnen || []).map(l => ({
                        ...l,
                        btw: (!l.btw || l.btw === String(oud.btw_pct) || l.btw === '21') ? String(nieuwBtw) : l.btw
                      }));
                    } else if (nieuwBtw === -1) {
                      // Vrijgesteld → 0% op alle regels
                      nieuweLijnen = (p.lijnen || []).map(l => ({ ...l, btw: '0' }));
                    }
                    return {
                      ...p,
                      categorie: nieuw,
                      grootboekrekening: nieuwGb,
                      btw_pct_cat: nieuwBtw,
                      aftrekbaarheid: (nw.aftrekbaarheid !== undefined) ? nw.aftrekbaarheid : p.aftrekbaarheid,
                      lijnen: nieuweLijnen,
                    };
                  });
                }} data-testid="vw-categorie">{CATEGORIE_GROEPEN.map(g => (<optgroup key={g.groep} label={g.groep}>{g.items.map(c => <option key={c} value={c}>{c}</option>)}</optgroup>))}</select></div>
                <div className="flex items-center gap-2"><span className="text-xs text-gray-500 w-28">BTW type *</span><select className="field-input flex-1" value={form.btw_type} onChange={u('btw_type')}>{BTW_TYPES.map(b => <option key={b} value={b}>{b}</option>)}</select></div>
                <div className="flex items-center gap-2"><span className="text-xs text-gray-500 w-28">IBAN leverancier</span><input className="field-input flex-1 font-mono text-xs" value={form.iban_lev} onChange={u('iban_lev')} data-testid="vw-iban" /></div>
                <div className="flex items-center gap-2"><span className="text-xs text-gray-500 w-28">Factuurnummer</span><input className="field-input flex-1" value={form.factuurnummer} onChange={u('factuurnummer')} data-testid="vw-facnr" /></div>
                <div className="flex items-center gap-2"><span className="text-xs text-gray-500 w-28">Datum *</span><input type="date" className="field-input flex-1" value={form.datum} onChange={u('datum')} data-testid="vw-datum" /></div>
                <div className="flex items-center gap-2"><span className="text-xs text-gray-500 w-28">Vervaldag</span><input type="date" className="field-input flex-1" value={form.vervaldag} onChange={u('vervaldag')} data-testid="vw-vervaldag" /></div>
                <div className="flex items-center gap-2"><span className="text-xs text-gray-500 w-28">Betaald</span><input type="date" className="field-input flex-1" value={form.betaald_datum} onChange={u('betaald_datum')} /></div>
                <div className="flex items-center gap-2"><span className="text-xs text-gray-500 w-28">Betaalwijze</span><select className="field-input flex-1" value={form.betaalwijze} onChange={u('betaalwijze')}>{BETAALWIJZEN_INK.map(b => <option key={b} value={b}>{b || '-- Kies --'}</option>)}</select></div>
                <div className="flex items-center gap-2"><span className="text-xs text-gray-500 w-28">Mededeling</span><input className="field-input flex-1" value={form.mededeling} onChange={u('mededeling')} /></div>
              </div>

              {/* Geavanceerd */}
              <details className="mt-3">
                <summary className="text-xs font-bold text-[#D97706] cursor-pointer">Geavanceerd</summary>
                <div className="space-y-2 mt-2 pl-2 border-l-2 border-[#D97706]/20">
                  <div className="flex items-center gap-2"><span className="text-xs text-gray-500 w-28">Grootboekrek.</span><input className="field-input flex-1" value={form.grootboekrekening} onChange={u('grootboekrekening')} data-testid="vw-gb" /></div>
                  {form.categorie && catMapping[form.categorie] && (
                    <div className="flex items-center gap-2 pl-[120px]" data-testid="vw-cat-info">
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-blue-100 text-blue-800">
                        BTW: {catMapping[form.categorie].btw_pct === -1 ? 'Vrijgesteld' : `${catMapping[form.categorie].btw_pct}%`}
                      </span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 ${
                        catMapping[form.categorie].aftrekbaarheid === 100 ? 'bg-green-100 text-green-800' :
                        catMapping[form.categorie].aftrekbaarheid === 0 ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        Aftrekbaarheid: {catMapping[form.categorie].aftrekbaarheid}%
                        {catMapping[form.categorie].aftrekbaarheid === 50 && ' (personenwagen)'}
                        {catMapping[form.categorie].aftrekbaarheid === 69 && ' (restaurant)'}
                      </span>
                      {form.aftrekbaarheid !== undefined && form.aftrekbaarheid !== catMapping[form.categorie].aftrekbaarheid && (
                        <span className="text-[10px] text-[#D97706] font-bold">
                          (handmatig: {form.aftrekbaarheid}%)
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2"><span className="text-xs text-gray-500 w-28">Onze referentie</span><input className="field-input flex-1" value={form.onze_referentie} onChange={u('onze_referentie')} /></div>
                  <div className="flex items-center gap-2"><span className="text-xs text-gray-500 w-28">Betreft</span><input className="field-input flex-1" value={form.betreft} onChange={u('betreft')} /></div>
                  <div className="flex items-center gap-2"><span className="text-xs text-gray-500 w-28">Munteenheid</span><select className="field-input flex-1" value={form.munteenheid} onChange={u('munteenheid')}><option value="EUR">EUR</option><option value="USD">USD</option><option value="GBP">GBP</option></select></div>
                  <div className="flex items-start gap-2"><span className="text-xs text-gray-500 w-28 pt-1">Interne info</span><textarea className="field-input flex-1 text-xs" rows={2} value={form.interne_info} onChange={u('interne_info')} /></div>
                </div>
              </details>

              {/* TOTAAL */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Totaal</span>
                  <button className="btn btn-ghost btn-sm" onClick={addLijn}><Plus size={12} /> Lijn</button>
                </div>
                <table className="w-full text-xs">
                  <thead><tr className="border-b"><th className="text-left py-1">Omschrijving</th><th style={{width:60}}>BTW%</th><th style={{width:80}} className="text-right">Bedrag</th><th style={{width:80}} className="text-right">Totaal</th><th style={{width:28}}></th></tr></thead>
                  <tbody>
                    {form.lijnen.map((l, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td><input className="field-input text-xs w-full" value={l.omschr} onChange={e => updLijn(i, 'omschr', e.target.value)} /></td>
                        <td><select className="field-input text-xs" value={l.btw} onChange={e => updLijn(i, 'btw', e.target.value)}>
                          <option value="0">0%</option><option value="6">6%</option><option value="12">12%</option><option value="21">21%</option>
                        </select></td>
                        <td><input type="number" className="field-input text-xs text-right" value={l.bedrag} onChange={e => updLijn(i, 'bedrag', e.target.value)} placeholder="0.00" /></td>
                        <td className="text-right font-medium">{fmt(parseFloat(l.totaal) || 0)}</td>
                        <td><button className="text-red-400 hover:text-red-600" onClick={() => rmLijn(i)}><Trash size={12} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-3 space-y-1 text-right text-xs">
                  <div className="flex justify-end gap-8"><span className="text-gray-500">Totaal exclusief</span><span className="font-medium w-24">{fmt(excl)}</span></div>
                  <div className="flex justify-end gap-8 items-center"><span className="text-gray-500">Fin. korting</span><input type="number" className="field-input text-xs text-right" style={{width:70}} value={form.financiele_korting} onChange={u('financiele_korting')} /><span className="text-gray-400">%</span></div>
                  <div className="flex justify-end gap-8"><span className="text-gray-500">BTW</span><span className="font-medium w-24">{fmt(btwBedrag)}</span></div>
                  <div className="flex justify-end gap-8 pt-2 border-t border-gray-200"><span className="font-bold">Totaal bedrag</span><span className="font-bold text-lg w-24">{fmt(totaal)}</span></div>
                </div>
              </div>

              {/* Opslaan */}
              <button className="w-full py-3 bg-[#D97706] text-white font-bold text-sm hover:bg-[#B45309] transition-colors mt-4" onClick={saveVerwerk} data-testid="vw-opslaan">
                Opslaan en volgende
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ═══ LIJSTWEERGAVE ═══
  return (
    <div data-testid="inkomende-docs-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="page-heading mb-0">Inkomende facturen</h1>
        <button className="btn btn-primary" onClick={() => setViewMode('upload')} data-testid="show-upload">
          <Plus size={14} weight="bold" /> Toevoegen
        </button>
      </div>

      {/* Upload modal */}
      {viewMode === 'upload' && (
        <div className="section-card mb-5">
          <div className="section-header"><span className="section-title">VOEG BESTANDEN TOE</span>
            <button className="btn btn-ghost btn-sm" onClick={() => { setViewMode('lijst'); setUploadMsg(null); }}>Sluiten</button>
          </div>
          <div className="p-5">
            {aiBezig && (
              <div className="bg-[#002FA7] text-white p-3 mb-3 flex items-center gap-3" data-testid="ink-ai-bezig">
                <CircleNotch size={22} className="animate-spin shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">AI leest factuur {aiBezig.index}/{aiBezig.totaal}…</div>
                  <div className="text-[11px] opacity-80 truncate">{aiBezig.naam} — leverancier, factuurnummer, datum, bedragen en categorie worden herkend. U hoeft enkel nog goed te keuren.</div>
                </div>
              </div>
            )}
            <div className={`border-2 border-dashed p-8 text-center cursor-pointer transition-all ${dragOver ? 'border-[#D97706] bg-[#FFF8E1] scale-[1.01]' : 'border-[#D97706]/40 bg-[#FFFBEB] hover:bg-[#FFF8E1]'}`}
              onDrop={handleDrop} onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
              onClick={() => !uploading && fileRef.current?.click()} data-testid="upload-dropzone">
              <UploadSimple size={32} weight="bold" className="text-[#D97706] mx-auto mb-2" />
              <p className="text-sm text-[#92400E] mb-1 font-medium">{uploading ? 'Bezig met uploaden en lezen...' : 'Sleep bestanden naar hier of klik om toe te voegen'}</p>
              <p className="text-[11px] text-[#92400E]/80 mb-3 inline-flex items-center gap-1"><Sparkle size={12} weight="fill" /> Na het toevoegen leest AI de factuur meteen — u hoeft enkel nog goed te keuren</p>
              <button type="button" className="px-5 py-2.5 bg-[#D97706] text-white font-bold text-sm hover:bg-[#B45309] inline-flex items-center gap-2 disabled:opacity-60" disabled={uploading}
                onClick={e => { e.stopPropagation(); if (!uploading) fileRef.current?.click(); }} data-testid="upload-btn">
                <UploadSimple size={16} weight="bold" /> {uploading ? 'Bezig...' : 'Bestand toevoegen'}
              </button>
              <input ref={fileRef} type="file" className="hidden" multiple accept=".pdf,.xml,.png,.jpg,.jpeg,.tif,.tiff,.heic,.zip,.csv,.xls,.xlsx,.doc,.docx"
                onChange={e => { upload(e.target.files); e.target.value = ''; }} data-testid="upload-input" />
            </div>
            {uploadMsg && (
              <div className={`mt-3 p-3 text-sm font-medium ${
                uploadMsg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800'
                : uploadMsg.type === 'warning' ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                : 'bg-red-50 border border-red-200 text-red-800'
              }`} data-testid="upload-msg">
                {uploadMsg.text}
              </div>
            )}
            <p className="text-[10px] text-gray-400 mt-2">PDF, XML, PNG, JPG, TIF, HEIC, ZIP, CSV, XLS, XLSX, DOC — max 20MB per bestand</p>
          </div>
        </div>
      )}

      {/* KPI's — zelfde stijl als facturen */}
      {(() => {
        const isTeVerwerken = (d) => !d.leverancier && (!d.status_doc || d.status_doc === 'te_verwerken');
        const isVerwerkt = (d) => d.status_doc === 'verwerkt' || (!!d.leverancier && (!d.status_doc || d.status_doc === 'te_verwerken')) || (d.dubbel_van && d.auto_goedgekeurd && !d.dubbel_bevestigd);
        const pc = {
          totaal: docs.length,
          te_verwerken: docs.filter(isTeVerwerken).length,
          verwerkt: docs.filter(isVerwerkt).length,
          goedgekeurd: docs.filter(d => d.status_doc === 'goedgekeurd').length,
          geboekt: docs.filter(d => d.status_doc === 'geboekt').length,
        };
        const vervallenCount = docs.filter(d => d.vervaldag && new Date(d.vervaldag) < new Date() && d.status_doc !== 'geboekt').length;
        return (
          <div className="document-summary-grid" data-testid="incoming-document-summaries">
            <DocumentSummary label="Totaal" value={pc.totaal} testId="pipeline-total" />
            {[
              { key: 'te_verwerken', label: 'Te verwerken', color: '#A16207', val: pc.te_verwerken },
              { key: 'verwerkt', label: 'Goed te keuren', color: '#2563EB', val: pc.verwerkt },
              { key: 'goedgekeurd', label: 'Goedgekeurd', color: '#008A27', val: pc.goedgekeurd },
              { key: 'geboekt', label: 'Geboekt', color: '#7C3AED', val: pc.geboekt },
              { key: 'vervallen_tab', label: 'Vervallen', color: '#E02424', val: vervallenCount },
            ].map(s => (
              <DocumentSummary key={s.key} label={s.label} value={s.val} color={s.color}
                onClick={() => setTab(tab === s.key ? 'alle' : s.key)} selected={tab === s.key}
                testId={`pipeline-${s.key}`} valueTestId={`pipeline-count-${s.key}`} />
            ))}
          </div>
        );
      })()}

      {/* Tabs — met Vervallen badge en Enkel bonnetjes */}
      <div className="flex gap-4 mb-3 items-center border-b border-gray-200">
        {(() => {
          const vervallenCount = docs.filter(d => d.vervaldag && new Date(d.vervaldag) < new Date() && d.status_doc !== 'geboekt' && d.status !== 'Betaald').length;
          const bonnetjesCount = docs.filter(d => d.doc_type === 'bonnetje').length;
          const tabs = [
            { key: 'alle', label: 'Alle', color: '#002FA7' },
            { key: 'vervallen_tab', label: 'Vervallen', color: '#E02424', badge: vervallenCount },
            { key: 'bonnetjes', label: 'Enkel bonnetjes', color: '#D97706', badge: bonnetjesCount },
          ];
          return tabs.map(t => (
            <button key={t.key} className={`text-sm font-bold pb-2 -mb-[1px] border-b-2 flex items-center gap-1.5 ${tab === t.key ? 'border-current' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
              style={tab === t.key ? { color: t.color } : {}}
              onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}>
              {t.label}
              {t.badge > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ background: tab === t.key ? t.color : '#94A3B8' }}>{t.badge}</span>}
            </button>
          ));
        })()}
      </div>

      {/* Zoekbalk + Filters toggle + actieve filter-chips */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative" style={{ width: 320 }}>
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input className="field-input w-full" style={{ paddingLeft: 36 }} placeholder="Zoek op leverancier, nummer, referentie..." value={q} onChange={e => setQ(e.target.value)} data-testid="ink-search" />
        </div>
        <button className={`btn btn-sm inline-flex items-center gap-1.5 ${filtersOpen || hasActiveFilters ? 'bg-[#D97706] text-white hover:bg-[#B45309]' : 'btn-ghost'}`}
          onClick={() => setFiltersOpen(o => !o)} data-testid="toggle-filters">
          <Funnel size={13} weight={filtersOpen || hasActiveFilters ? 'fill' : 'regular'} /> Filters
          {hasActiveFilters && <span className="text-[10px] bg-white text-[#D97706] px-1.5 py-0.5 rounded-full font-bold">{[fLeverancier, fDatumVan, fDatumTot, fMin, fMax, fBetaalwijze, fCategorie, statusF, fMap].filter(Boolean).length}</span>}
        </button>
        {/* Inline chip-rij van actieve filters */}
        {hasActiveFilters && (
          <div className="flex items-center gap-1.5 flex-wrap text-xs" data-testid="active-chips">
            {fLeverancier && <span className="inline-flex items-center gap-1 pl-2 pr-0.5 py-0.5 bg-[#FFFBEB] border border-[#D97706]/30 text-[#92400E] font-medium">Leverancier: <span className="font-bold">{fLeverancier}</span><button className="px-1 text-[#92400E]/60 hover:text-[#92400E]" onClick={() => setFLeverancier('')} data-testid="chip-x-leverancier">×</button></span>}
            {statusF && <span className="inline-flex items-center gap-1 pl-2 pr-0.5 py-0.5 bg-[#FFFBEB] border border-[#D97706]/30 text-[#92400E] font-medium">Status: <span className="font-bold">{statusF}</span><button className="px-1 text-[#92400E]/60 hover:text-[#92400E]" onClick={() => setStatusF('')} data-testid="chip-x-status">×</button></span>}
            {fMap && <span className="inline-flex items-center gap-1 pl-2 pr-0.5 py-0.5 bg-[#FFFBEB] border border-[#D97706]/30 text-[#92400E] font-medium">Map: <span className="font-bold">{boekmapInfo(fMap)?.label}</span><button className="px-1 text-[#92400E]/60 hover:text-[#92400E]" onClick={() => setFMap('')} data-testid="chip-x-map">×</button></span>}
            {(fDatumVan || fDatumTot) && (
              <span className="inline-flex items-center gap-1 pl-2 pr-0.5 py-0.5 bg-[#FFFBEB] border border-[#D97706]/30 text-[#92400E] font-medium">
                Datum: <span className="font-bold">{fDatumVan ? new Date(fDatumVan).toLocaleDateString('nl-BE', {day:'2-digit',month:'2-digit'}) : '…'} – {fDatumTot ? new Date(fDatumTot).toLocaleDateString('nl-BE', {day:'2-digit',month:'2-digit'}) : '…'}</span>
                <button className="px-1 text-[#92400E]/60 hover:text-[#92400E]" onClick={() => { setFDatumVan(''); setFDatumTot(''); }} data-testid="chip-x-datum">×</button>
              </span>
            )}
            {(fMin || fMax) && (
              <span className="inline-flex items-center gap-1 pl-2 pr-0.5 py-0.5 bg-[#FFFBEB] border border-[#D97706]/30 text-[#92400E] font-medium">
                Bedrag: <span className="font-bold">€{fMin || '0'} – €{fMax || '∞'}</span>
                <button className="px-1 text-[#92400E]/60 hover:text-[#92400E]" onClick={() => { setFMin(''); setFMax(''); }} data-testid="chip-x-bedrag">×</button>
              </span>
            )}
            {fBetaalwijze && <span className="inline-flex items-center gap-1 pl-2 pr-0.5 py-0.5 bg-[#FFFBEB] border border-[#D97706]/30 text-[#92400E] font-medium">Betaalwijze: <span className="font-bold">{fBetaalwijze}</span><button className="px-1 text-[#92400E]/60 hover:text-[#92400E]" onClick={() => setFBetaalwijze('')} data-testid="chip-x-betaalwijze">×</button></span>}
            {fCategorie && <span className="inline-flex items-center gap-1 pl-2 pr-0.5 py-0.5 bg-[#FFFBEB] border border-[#D97706]/30 text-[#92400E] font-medium" title={fCategorie}>Categorie: <span className="font-bold truncate max-w-[180px]">{fCategorie}</span><button className="px-1 text-[#92400E]/60 hover:text-[#92400E]" onClick={() => setFCategorie('')} data-testid="chip-x-categorie">×</button></span>}
            <button className="text-[11px] text-red-500 hover:text-red-700 font-bold ml-1 underline" onClick={clearFilters} data-testid="clear-filters">Alles wissen</button>
          </div>
        )}
        <div className="ml-auto text-xs text-gray-400 whitespace-nowrap">{filtered.length} van {docs.length}</div>
      </div>

      {/* Totaal openstaand banner */}
      {totaalOpenstaand.bedrag > 0 && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 mb-3 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500" data-testid="totaal-openstaand-banner">
          <div className="flex items-center gap-2">
            <CurrencyEur size={18} weight="fill" className="text-amber-700" />
            <span className="text-xs uppercase tracking-wider text-amber-700 font-bold">Totaal openstaand</span>
          </div>
          <span className="text-2xl font-black text-amber-900 font-mono" data-testid="totaal-openstaand-bedrag">{fmt(totaalOpenstaand.bedrag)}</span>
          <span className="text-xs text-amber-700">over <strong>{totaalOpenstaand.aantal}</strong> {totaalOpenstaand.aantal === 1 ? 'factuur' : 'facturen'}</span>
        </div>
      )}

      {/* Uitgebreide filter-balk */}
      {filtersOpen && (
        <div className="section-card mb-3" data-testid="filter-panel">
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-9 gap-2 items-end [&>div]:min-w-0 [&_input]:w-full [&_select]:w-full">
              <div>
                <label className="text-[10px] text-gray-400 uppercase font-bold block mb-0.5">Leverancier</label>
                <input className="field-input" placeholder="Zoek..." value={fLeverancier} onChange={e => setFLeverancier(e.target.value)} data-testid="f-leverancier" />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 uppercase font-bold block mb-0.5">Status</label>
                <select className="field-input" value={statusF} onChange={e => setStatusF(e.target.value)} data-testid="f-status">
                  <option value="">-- Alle --</option>
                  {STATUSSEN.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-gray-400 uppercase font-bold block mb-0.5">Map</label>
                <select className="field-input" value={fMap} onChange={e => setFMap(e.target.value)} data-testid="f-map" title="Kosten gaan naar het Kostenrapport; aankoop goederen en investeringen niet">
                  <option value="">-- Alle --</option>
                  {BOEKMAPPEN.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-gray-400 uppercase font-bold block mb-0.5">Van</label>
                <input type="date" className="field-input" value={fDatumVan} onChange={e => setFDatumVan(e.target.value)} data-testid="f-datum-van" />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 uppercase font-bold block mb-0.5">Tot</label>
                <input type="date" className="field-input" value={fDatumTot} onChange={e => setFDatumTot(e.target.value)} data-testid="f-datum-tot" />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 uppercase font-bold block mb-0.5">€ Min.</label>
                <input type="number" className="field-input" placeholder="0" value={fMin} onChange={e => setFMin(e.target.value)} data-testid="f-min" />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 uppercase font-bold block mb-0.5">€ Max.</label>
                <input type="number" className="field-input" placeholder="∞" value={fMax} onChange={e => setFMax(e.target.value)} data-testid="f-max" />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 uppercase font-bold block mb-0.5">Betaalwijze</label>
                <select className="field-input" value={fBetaalwijze} onChange={e => setFBetaalwijze(e.target.value)} data-testid="f-betaalwijze">
                  <option value="">-- Alle --</option>
                  {BETAALWIJZEN_INK.filter(Boolean).map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-gray-400 uppercase font-bold block mb-0.5">Categorie</label>
                <select className="field-input" value={fCategorie} onChange={e => setFCategorie(e.target.value)} data-testid="f-categorie">
                  <option value="">-- Alle --</option>
                  {CATEGORIE_GROEPEN.map(g => (
                    <optgroup key={g.groep} label={g.groep}>
                      <option value={g.groep}>{`${g.groep} (alle)`}</option>
                      {g.items.map(c => <option key={c} value={c}>{c}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk-actie balk — altijd zichtbaar */}
      {(() => {
        const hasSel = selected.size > 0;
        const disabledStyle = hasSel ? '' : 'opacity-40 cursor-not-allowed';
        return (
          <div className={`flex flex-wrap items-center gap-2 mb-3 p-2 bg-[#002FA7]/5 border-l-4 ${hasSel ? 'border-[#002FA7]' : 'border-gray-300'}`} data-testid="bulk-bar">
            <span className={`text-sm font-bold ${hasSel ? 'text-[#002FA7]' : 'text-gray-400'}`}>
              {hasSel ? `${selected.size} geselecteerd` : 'Geen selectie'}
            </span>
            <button className={`btn btn-sm text-white font-bold ${disabledStyle}`} style={{ background: '#059669' }} onClick={bulkMarkBetaald} disabled={!hasSel || paymentBusy} data-testid="bulk-betalen">
              <CurrencyEur size={13} weight="bold" /> {paymentBusy ? 'Bevestigen…' : 'Betaling bevestigen'}
            </button>
            <button className={`btn btn-sm bg-[#D97706] text-white font-bold hover:bg-[#B45309] ${disabledStyle}`} onClick={bulkSetCategorie} disabled={!hasSel} data-testid="bulk-categorie">
              <FileText size={13} weight="bold" /> Categorie toewijzen
            </button>
            <button
              className={`btn btn-sm text-white font-bold inline-flex items-center gap-1 ${selected.size < 2 ? 'opacity-40 cursor-not-allowed' : ''}`}
              style={{ background: '#7C3AED' }}
              onClick={bulkMerge}
              disabled={selected.size < 2 || mergingBulk}
              title="Voeg geselecteerde PDF's samen tot 1 nieuw document (duplicaten worden automatisch overgeslagen via SHA-256)"
              data-testid="bulk-merge-pdf"
            >
              <FilePdf size={13} weight="bold" /> {mergingBulk ? 'Samenvoegen...' : 'Samenvoegen tot 1 PDF'}
            </button>
            <button className={`btn btn-sm text-red-600 hover:bg-red-50 ${disabledStyle}`} onClick={bulkDelete} disabled={!hasSel} data-testid="bulk-delete">
              <Trash size={13} /> Verwijderen
            </button>
            <div className="relative" ref={meerRef}>
              <button className={`btn btn-sm bg-white border border-[#002FA7] text-[#002FA7] font-bold hover:bg-[#002FA7]/5 inline-flex items-center gap-1 ${disabledStyle}`} onClick={() => hasSel && setMeerOpen(o => !o)} disabled={!hasSel} data-testid="bulk-meer-opties">
                Meer opties <DotsThreeVertical size={13} weight="bold" />
              </button>
              {meerOpen && hasSel && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 shadow-lg z-20 min-w-[200px]" data-testid="bulk-meer-menu">
                  <button className="w-full px-3 py-2 text-left text-sm hover:bg-[#FFFBEB] flex items-center gap-2 text-[#92400E]" onClick={bulkExport} data-testid="bulk-exporteren">
                    <Export size={14} weight="bold" className="text-[#D97706]" /> Exporteren (CSV)
                  </button>
                  <button className="w-full px-3 py-2 text-left text-sm hover:bg-[#FFFBEB] flex items-center gap-2 text-[#92400E]" onClick={bulkDownload} data-testid="bulk-download">
                    <DownloadSimple size={14} weight="bold" className="text-[#D97706]" /> Download bestanden
                  </button>
                  <button className="w-full px-3 py-2 text-left text-sm hover:bg-[#FFFBEB] flex items-center gap-2 text-[#92400E]" onClick={bulkPrint} data-testid="bulk-printen">
                    <Printer size={14} weight="bold" className="text-[#D97706]" /> Printen
                  </button>
                  <button className="w-full px-3 py-2 text-left text-sm hover:bg-[#FFFBEB] flex items-center gap-2 text-[#92400E] border-t border-gray-100" onClick={bulkToggleBetaald} data-testid="bulk-toggle-betaald">
                    <CheckCircle size={14} weight="bold" className="text-[#D97706]" /> Betaling bevestigen
                  </button>
                </div>
              )}
            </div>
            {hasSel && <button className="btn btn-ghost btn-sm ml-auto" onClick={() => setSelected(new Set())}>Selectie wissen</button>}
          </div>
        );
      })()}

      {/* 📥 Nog te verwerken uploads — ruwe documenten zonder leverancier-info */}
      {nogTeVerwerkenRijen.length > 0 && (
        <div className="mb-4 border border-amber-200 bg-amber-50/40" data-testid="nog-te-verwerken-sectie">
          <div className="flex items-center justify-between px-3 py-2 bg-amber-100/60 border-b border-amber-200">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-amber-900">📥 Nog te verwerken uploads</span>
              <span className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-200 text-amber-900 rounded">{nogTeVerwerkenRijen.length}</span>
            </div>
            <div className="text-[11px] text-amber-800 italic">AI kon deze bestanden niet lezen — klik &apos;Verwerken&apos; om ze zelf in te vullen</div>
          </div>
          <table className="data-table">
            <thead className="bg-amber-50">
              <tr>
                <th>Bestand</th>
                <th>Geüpload op</th>
                <th>Bron</th>
                <th className="text-right">Actie</th>
              </tr>
            </thead>
            <tbody>
              {nogTeVerwerkenRijen.map(d => (
                <tr key={d.id} className="border-t border-amber-100 hover:bg-amber-50/60" data-testid={`onverwerkt-row-${d.id}`}>
                  <td className="font-mono text-xs">
                    <button onClick={() => startVerwerk(d)} className="text-[#002FA7] hover:underline font-semibold">
                      📄 {d.filename || d.id.slice(0, 12)}
                    </button>
                  </td>
                  <td className="text-xs text-gray-600">{d.created_at ? new Date(d.created_at).toLocaleString('nl-BE') : '—'}</td>
                  <td className="text-xs text-gray-600">{d.bron || 'upload'}</td>
                  <td className="text-right">
                    <button onClick={() => startVerwerk(d)} className="btn btn-sm btn-primary inline-flex items-center gap-1" data-testid={`onverwerkt-open-${d.id}`}>
                      <MagicWand size={12} weight="bold" /> Verwerken
                    </button>
                    <button onClick={() => delDoc(d.id)} className="btn btn-sm text-red-600 hover:bg-red-50 ml-1" title="Verwijderen" data-testid={`onverwerkt-del-${d.id}`}>
                      <Trash size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tabel */}
      <div className="section-card">
        <div className="section-body overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{width:30}}>
                  <input type="checkbox" className="accent-[#002FA7]" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleSelAll} data-testid="select-all" />
                </th>
                <th style={{width:30}}></th>
                <th>Nr</th>
                <th>Datum</th>
                <th>Vervaldag</th>
                <th>Referentie</th>
                <th>Betreft</th>
                <th>Leverancier</th>
                <th className="right">Excl.</th>
                <th className="right">BTW</th>
                <th className="right">Totaal</th>
                <th className="right">Te betalen</th>
                <th>Status</th>
                <th><DocumentActionHeader type="inkomend" preferences={actionPreferences} /></th>
              </tr>
            </thead>
            <tbody>
              {pagedVerwerkt.map(d => {
                const isVervallen = d.vervaldag && new Date(d.vervaldag) < new Date() && d.status !== 'Betaald';
                const dagenOver = d.vervaldag ? Math.max(0, Math.floor((new Date() - new Date(d.vervaldag)) / 86400000)) : 0;
                // Status-pill styling (consistent met rest van app)
                const STATUS_MAP = {
                  'Betaald':          { bg: '#16A34A', icon: '✓', label: 'BETAALD' },
                  'Deels betaald':    { bg: '#0284C7', icon: '◐', label: 'DEELS BETAALD' },
                  'Te Domiciliëren':  { bg: '#7C3AED', icon: '⟲', label: 'TE DOMICILIËREN' },
                  'Te Betalen':       { bg: '#D97706', icon: '◐', label: 'TE BETALEN' },
                };
                const DOC_MAP = {
                  'goedgekeurd': { bg: '#16A34A', icon: '✓', label: 'GOEDGEKEURD' },
                  'geboekt':     { bg: '#7C3AED', icon: '✓', label: 'GEBOEKT' },
                  'verwerkt':    { bg: '#002FA7', icon: '→', label: 'VERWERKT' },
                  'afgekeurd':   { bg: '#DC2626', icon: '✕', label: 'AFGEKEURD' },
                };
                let pill = STATUS_MAP[betaalStatusLabel(d)] || DOC_MAP[d.status_doc] || (d.leverancier ? DOC_MAP['verwerkt'] : null) || { bg: '#6B7280', icon: '✎', label: (d.status || 'CONCEPT').toUpperCase() };
                const checked = selected.has(d.id);
                return (
                  <tr key={d.id || d.factuurnummer} className={`cursor-pointer hover:bg-[#F0F4FF] ${checked ? 'bg-[#F0F4FF]' : ''}`} onClick={() => startVerwerk(d)} data-testid={`row-${d.id}`}>
                    <td onClick={e => e.stopPropagation()}>
                      <input type="checkbox" className="accent-[#002FA7]" checked={checked} onChange={() => toggleSel(d.id)} data-testid={`select-${d.id}`} />
                    </td>
                    <td className="flex items-center gap-1">
                      {d.type === 'pdf' && <FilePdf size={14} className="text-[#DC2626]" weight="fill" />}
                      {['jpg','jpeg','png','tif','heic','gif'].includes(d.type) && <FileImage size={14} className="text-[#2563EB]" weight="fill" />}
                      {d.type === 'xml' && <FileText size={14} className="text-[#059669]" weight="fill" />}
                    </td>
                    <td className="font-mono text-xs font-bold text-[#002FA7]">{d.factuurnummer || d.id || '-'}</td>
                    <td className="text-xs">{d.datum || '-'}</td>
                    <td className="text-xs">{d.vervaldag || '-'}</td>
                    <td className="text-xs">{d.onze_referentie || '-'}</td>
                    <td className="text-xs truncate max-w-[200px]">{d.betreft || d.filename || '-'}</td>
                    <td className="font-medium text-sm">
                      {d.leverancier || '-'}
                      {d.is_peppol && <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 bg-cyan-100 text-cyan-700" title="Peppol/UBL">PEPPOL</span>}
                      {d.dubbel_van && (
                        <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 bg-red-600 text-white border border-red-700"
                          title={`Mogelijk dubbel: ${d.dubbel_van.leverancier} ${d.dubbel_van.factuurnummer || ''} bestaat al als ${d.dubbel_van.bron === 'kost' ? 'kost' : 'inkomende factuur'} (${d.dubbel_van.datum || '—'})`}
                          data-testid={`dubbel-badge-${d.id}`}>
                          <Warning size={10} weight="fill" /> DUBBEL?
                        </span>
                      )}
                      <KostBadge d={d} />
                    </td>
                    <td className="right text-xs">{d.excl ? fmt(d.excl) : '-'}</td>
                    <td className="right text-xs">{d.btw_bedrag ? fmt(d.btw_bedrag) : '-'}</td>
                    <td className="right text-xs font-bold">{d.totaal ? fmt(d.totaal) : '-'}</td>
                    <td className="right text-xs font-bold" data-testid={`openstaand-${d.id}`}>
                      <span className={d.status_betaling === 'betaald' ? 'text-green-600' : ''}>{fmt(d.openstaand ?? 0)}</span>
                      {d.betaald_bedrag > 0 && <span className="block text-[10px] font-normal text-emerald-700" data-testid={`betaald-bedrag-${d.id}`}>{fmt(d.betaald_bedrag)} betaald</span>}
                    </td>
                    <td>
                      <div className="flex items-center gap-1 flex-wrap">
                        <span data-testid={`betaalstatus-${d.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 whitespace-nowrap tracking-wider" style={{ background: pill.bg, color: '#fff', lineHeight: 1.2 }}>
                          <span>{pill.icon}</span>
                          <span>{pill.label}</span>
                        </span>
                        {isVervallen && dagenOver > 0 && d.status !== 'Betaald' && <span className="text-[9px] font-bold px-1.5 py-0.5 bg-red-500 text-white">{dagenOver}d</span>}
                        {(d.leverancier && (!d.status_doc || ['te_verwerken', 'verwerkt'].includes(d.status_doc))) ? (
                          // AI-gelezen, nog goed te keuren: kies de map en keur direct goed vanuit de lijst
                          <span className="inline-flex items-center gap-1" onClick={e => e.stopPropagation()}>
                            <select className="text-[10px] font-bold border border-gray-300 bg-white px-1 py-0.5 cursor-pointer" value={d.boekmap || 'kosten'} onChange={e => zetBoekmap(d, e.target.value, e)} title="Map: enkel 'Kosten' komt in het Kostenrapport" data-testid={`map-select-${d.id}`}>
                              {BOEKMAPPEN.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
                            </select>
                          </span>
                        ) : (d.boekmap && boekmapInfo(d.boekmap) && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 text-white tracking-wider" style={{ background: boekmapInfo(d.boekmap).bg }} title={`Map: ${boekmapInfo(d.boekmap).label}`} data-testid={`map-badge-${d.id}`}>
                            {boekmapInfo(d.boekmap).kort}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <InvoiceRowActions docType="inkomend" documentId={d.id} primaryActions={actionPreferences.primary_actions}>
                        {actionPreferences.visible.edit && <button type="button" onClick={() => startVerwerk(d)} title="Document openen" data-testid={`incoming-open-${d.id}`}><PencilSimple size={14} /></button>}
                        {actionPreferences.visible.pay && d.openstaand > 0 && d.status_doc !== 'afgekeurd' && d.doc_type !== 'creditnota' && <button type="button" onClick={() => setPaymentDoc(d)} title="Betaling bevestigen" data-testid={`betaling-bevestigen-${d.id}`}><CurrencyEur size={14} /></button>}
                        {actionPreferences.visible.approve && d.leverancier && (!d.status_doc || ['te_verwerken', 'verwerkt'].includes(d.status_doc)) && <button type="button" onClick={e => snelGoedkeuren(d, e)} title="Goedkeuren" data-testid={`snel-goedkeur-${d.id}`}><CheckCircle size={14} /></button>}
                      </InvoiceRowActions>
                    </td>
                  </tr>
                );
              })}
              {/* Niet-verwerkte docs */}
              {filtered.filter(d => !d.status_doc && !d.leverancier).map(d => (
                <tr key={d.id} className="bg-[#FFFBEB] cursor-pointer hover:bg-[#FEF3C7]" onClick={() => startVerwerk(d)} data-testid={`row-${d.id}`}>
                  <td onClick={e => e.stopPropagation()}>
                    <input type="checkbox" className="accent-[#002FA7]" checked={selected.has(d.id)} onChange={() => toggleSel(d.id)} />
                  </td>
                  <td>
                    {d.type === 'pdf' && <FilePdf size={14} className="text-[#DC2626]" weight="fill" />}
                    {['jpg','jpeg','png','tif','heic','gif'].includes(d.type) && <FileImage size={14} className="text-[#2563EB]" weight="fill" />}
                    {d.type === 'xml' && <FileText size={14} className="text-[#059669]" weight="fill" />}
                  </td>
                  <td className="text-xs text-gray-400">{d.id}</td>
                  <td className="text-xs">{(d.uploaded_at || '').split('T')[0]}</td>
                  <td colSpan="4" className="text-xs text-[#D97706] font-medium">{d.filename} — Te verwerken</td>
                  <td colSpan="4" className="text-xs text-gray-400">{d.type?.toUpperCase()}</td>
                  <td><span className="text-[10px] font-bold px-2 py-0.5 bg-yellow-100 text-yellow-800">Te verwerken</span></td><td />
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="14" className="text-center text-gray-400 py-8">Geen inkomende facturen</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination total={verwerkteRijen.length} pageSize={pageSize} setPageSize={setPageSize} curPage={curPage} setPage={setPage} />
      </div>
      {/* Categorie bulk-toewijs modal */}
      {paymentDoc && <InkomendeBetalingDialog doc={paymentDoc} onClose={() => setPaymentDoc(null)} onSaved={() => { setSelected(new Set()); load(); }} />}
      {catModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" onClick={() => setCatModalOpen(false)} data-testid="cat-modal">
          <div className="bg-white w-[90%] max-w-md shadow-xl border border-gray-200" onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <span className="text-sm font-bold text-[#002FA7]">Categorie toewijzen aan {selected.size} document(en)</span>
              <button className="text-gray-400 hover:text-gray-600" onClick={() => setCatModalOpen(false)}>×</button>
            </div>
            <div className="p-4">
              <label className="text-xs text-gray-500 block mb-1">Kies een categorie</label>
              <select className="field-input w-full" value={catModalValue} onChange={e => setCatModalValue(e.target.value)} autoFocus data-testid="cat-modal-select">
                <option value="">-- Kies categorie --</option>
                {CATEGORIE_GROEPEN.map(g => (
                  <optgroup key={g.groep} label={g.groep}>
                    {g.items.map(c => <option key={c} value={c}>{c}</option>)}
                  </optgroup>
                ))}
              </select>
              <div className="mt-4 flex justify-end gap-2">
                <button className="btn btn-ghost btn-sm" onClick={() => setCatModalOpen(false)}>Annuleren</button>
                <button className="btn btn-primary btn-sm" onClick={applyCategorie} data-testid="cat-modal-apply">Toepassen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
